import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlertCircle,
    Bookmark,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Clock,
    Heart,
    Info,
    MessageCircle,
    MoreHorizontal,
    Music,
    PauseCircle,
    RefreshCw,
    Repeat2,
    Send,
    Share2,
    SquarePen,
    ThumbsUp,
    Trash2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { SocialPost } from '../../../../common/models/social-scheduler.model';
import { formatPostTimeShort } from '../format-time';

interface PostCardProps {
    post: SocialPost;
    /** Resolved asset URLs keyed by asset UUID */
    assetUrlMap?: Record<string, string>;
    /** Asset kind ('image' | 'video') keyed by asset UUID — videos render via <video>, others as background image */
    assetKindMap?: Record<string, 'image' | 'video' | 'upload'>;
    accountName?: string;
    accountAvatar?: string;
    /** Asset URLs are still loading — show skeleton instead of placeholder */
    assetsLoading?: boolean;
    /** Accounts are still loading — show skeleton avatar/name */
    accountLoading?: boolean;
    onClick?: (uuid: string) => void;
    onDelete?: (uuid: string) => void;
}

// Status tint + text from the shared token set so each status reads in both themes.
const statusStyles: Record<string, { bar: string; text: string }> = {
    draft: { bar: 'bg-muted', text: 'text-muted-foreground' },
    scheduled: { bar: 'bg-muted', text: 'text-muted-foreground' },
    on_hold: { bar: 'bg-warning/10', text: 'text-warning' },
    publishing: { bar: 'bg-warning/10', text: 'text-warning' },
    published: { bar: 'bg-success/10', text: 'text-success' },
    failed: { bar: 'bg-destructive/10', text: 'text-destructive' },
};

const statusIcons: Record<string, React.ElementType> = {
    draft: SquarePen,
    scheduled: Clock,
    on_hold: PauseCircle,
    publishing: RefreshCw,
    published: CheckCircle2,
    failed: AlertCircle,
};

// The previews imitate each network's own UI, which is set in the platform's
// system font — not ours to restyle to the app face.
const NATIVE_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/**
 * Renders a post preview mimicking the native social media platform UI.
 * Supports multi-image carousel with navigation arrows and dot indicators.
 */
export const PostCard: React.FC<PostCardProps> = ({
    post,
    assetUrlMap = {},
    assetKindMap = {},
    accountName,
    accountAvatar,
    assetsLoading = false,
    accountLoading = false,
    onClick,
    onDelete,
}) => {
    const { t } = useTranslation();
    const styles = statusStyles[post.status] || statusStyles.draft;
    const StatusIcon = statusIcons[post.status];
    // Normalize: some account names already start with '@' (e.g. '@concept-mosaique')
    const rawName = accountName || 'account';
    const handle = rawName.replace(/^@+/, '');
    // Use published time for published posts, scheduled time otherwise
    const timestampIso = post.status === 'published' && post.publishedAt ? post.publishedAt : post.scheduledAt;
    const scheduledDate = formatPostTimeShort(timestampIso);
    const absoluteDate = new Date(timestampIso).toLocaleString();

    // Resolve image URLs and kinds from the post's asset list (parallel arrays)
    const allUuids = (post.assetUuids?.length ? post.assetUuids : [post.assetUuid]).filter(Boolean);
    const resolved = allUuids
        .map((uuid) => ({ url: assetUrlMap[uuid], kind: assetKindMap[uuid] }))
        .filter((m) => !!m.url);
    const imageUrls = resolved.map((m) => m.url) as string[];
    const imageKinds = resolved.map((m) => m.kind) as (string | undefined)[];

    const captionWithTags = [
        post.caption || '',
        post.hashtags?.length ? post.hashtags.map((h) => `#${h}`).join(' ') : '',
    ].filter(Boolean).join('\n');

    const isDraft = post.status === 'draft';
    const isOnHold = post.status === 'on_hold';
    const isInactive = isDraft || isOnHold;

    /*
     * Published is deletable too, and the server decides whether it really is:
     * it asks the platform first and refuses if the platform refuses. Hiding
     * the control here instead would leave a vendor unable to remove a post
     * from the one place they manage their posts.
     */
    const canDelete = !!onDelete && ['draft', 'scheduled', 'failed', 'published'].includes(post.status);

    return (
        <div
            onClick={() => onClick?.(post.uuid)}
            className={cn(
                'group relative overflow-hidden rounded-xl bg-card transition-all',
                isOnHold ? 'border-2 border-dashed border-warning' : isDraft ? 'border-2 border-dashed border-border' : 'border',
                isInactive && 'opacity-65 grayscale-[30%]',
                onClick && 'cursor-pointer hover:-translate-y-px hover:opacity-100 hover:grayscale-0',
            )}
        >
            {/* ── Status bar ─── */}
            <div className={cn('flex items-center justify-between border-b px-3 py-1.5', styles.bar)}>
                <div className="flex items-center gap-1.5">
                    {StatusIcon && (
                        <StatusIcon
                            aria-hidden
                            className={cn('size-3.5', styles.text, post.status === 'publishing' && 'animate-spin')}
                        />
                    )}
                    <span className={cn('text-[11px] font-medium', styles.text)}>
                        {t(`SOCIAL_SCHEDULER.STATUS.${post.status.toUpperCase()}`, post.status)}
                    </span>
                    {isOnHold && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={t('SOCIAL_SCHEDULER.ON_HOLD_REASON', 'This post is on hold because the linked social account has been disabled.') || ''}
                                    className="cursor-help text-warning"
                                >
                                    <Info className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[260px]">
                                {t('SOCIAL_SCHEDULER.ON_HOLD_REASON', 'This post is on hold because the linked social account has been disabled. Re-enable the account to restore scheduling.')}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className={cn('flex cursor-help items-center gap-1 text-[11px]', styles.text)}>
                            <Clock className="size-3" />
                            {scheduledDate}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">{absoluteDate}</TooltipContent>
                </Tooltip>
            </div>

            {/* ── Platform preview ─── */}
            {post.platform === 'instagram' && (
                <InstagramPreview handle={handle} avatar={accountAvatar} imageUrls={imageUrls} imageKinds={imageKinds} caption={captionWithTags} assetsLoading={assetsLoading} accountLoading={accountLoading} />
            )}
            {post.platform === 'meta' && (
                <FacebookPreview handle={handle} avatar={accountAvatar} imageUrls={imageUrls} imageKinds={imageKinds} caption={captionWithTags} assetsLoading={assetsLoading} accountLoading={accountLoading} />
            )}
            {post.platform === 'tiktok' && (
                <TikTokPreview handle={handle} imageUrls={imageUrls} imageKinds={imageKinds} caption={captionWithTags} assetsLoading={assetsLoading} accountLoading={accountLoading} />
            )}
            {post.platform === 'linkedin' && (
                <LinkedInPreview handle={handle} avatar={accountAvatar} imageUrls={imageUrls} imageKinds={imageKinds} caption={captionWithTags} assetsLoading={assetsLoading} accountLoading={accountLoading} />
            )}

            {/* ── Hover actions ─── */}
            {canDelete && (
                <div className="absolute end-2 top-2 z-[2] flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onDelete!(post.uuid); }}
                                aria-label={t('SOCIAL_SCHEDULER.DELETE_POST', 'Delete post') || 'Delete post'}
                                className="flex size-8 items-center justify-center rounded-full border bg-background/90 text-muted-foreground backdrop-blur transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                            >
                                <Trash2 className="size-4" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>{t('SOCIAL_SCHEDULER.DELETE', 'Delete')}</TooltipContent>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

/* ─── Image Carousel ────────────────────────────────────────── */

const ImageCarousel: React.FC<{ urls: string[]; kinds?: (string | undefined)[]; aspectRatio?: string; loading?: boolean }> = ({ urls, kinds, aspectRatio = '1/1', loading = false }) => {
    const [idx, setIdx] = useState(0);
    const count = urls.length;
    const hasMultiple = count > 1;
    const currentKind = kinds?.[idx];
    const isVideo = currentKind === 'video';

    const prev = (e: React.MouseEvent) => { e.stopPropagation(); setIdx((i) => (i - 1 + count) % count); };
    const next = (e: React.MouseEvent) => { e.stopPropagation(); setIdx((i) => (i + 1) % count); };

    if (loading) {
        return <Skeleton className="w-full" style={{ aspectRatio }} />;
    }

    if (count === 0) {
        return (
            <div className="flex w-full items-center justify-center bg-muted text-3xl text-muted-foreground" style={{ aspectRatio }}>
                🖼
            </div>
        );
    }

    return (
        <div className="relative w-full overflow-hidden bg-muted">
            {isVideo ? (
                <video
                    key={urls[idx]}
                    src={urls[idx]}
                    muted
                    autoPlay
                    loop
                    playsInline
                    className="block w-full bg-black object-cover"
                    style={{ aspectRatio }}
                />
            ) : (
                <div
                    className="w-full bg-cover bg-center"
                    style={{ aspectRatio, backgroundImage: `url(${urls[idx]})` }}
                />
            )}
            {hasMultiple && (
                <>
                    {/* Left arrow */}
                    <button
                        type="button"
                        onClick={prev}
                        className="absolute start-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border bg-background text-foreground transition-colors hover:bg-accent"
                    >
                        <ChevronLeft className="size-4" />
                    </button>
                    {/* Right arrow */}
                    <button
                        type="button"
                        onClick={next}
                        className="absolute end-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border bg-background text-foreground transition-colors hover:bg-accent"
                    >
                        <ChevronRight className="size-4" />
                    </button>
                    {/* Dot indicators */}
                    <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
                        {urls.map((_, i) => (
                            <span
                                key={i}
                                className={cn(
                                    'size-1.5 rounded-full shadow-sm transition-colors',
                                    i === idx ? 'bg-foreground' : 'bg-white/70',
                                )}
                            />
                        ))}
                    </div>
                    {/* Counter badge */}
                    <div
                        className="absolute end-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white"
                        style={{ fontFamily: NATIVE_FONT }}
                    >
                        {idx + 1}/{count}
                    </div>
                </>
            )}
        </div>
    );
};

/* ─── Shared helpers ────────────────────────────────────────── */

const Avatar: React.FC<{ src?: string; handle: string; size?: number }> = ({ src, handle, size = 32 }) => (
    <div
        className="flex shrink-0 items-center justify-center rounded-full bg-muted bg-cover bg-center font-medium text-muted-foreground"
        style={{ width: size, height: size, fontSize: size * 0.4, backgroundImage: src ? `url(${src})` : undefined }}
    >
        {!src && handle.charAt(0).toUpperCase()}
    </div>
);

const CaptionText: React.FC<{ text: string; lines?: number; fontSize?: number; color?: string }> = ({
    text, lines = 3, fontSize = 13, color,
}) => (
    <div
        className="overflow-hidden break-words whitespace-pre-wrap text-foreground"
        style={{
            fontSize,
            fontFamily: NATIVE_FONT,
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: lines,
            WebkitBoxOrient: 'vertical',
            color,
        }}
    >
        {text}
    </div>
);

/* ═══ INSTAGRAM ═══════════════════════════════════════════════ */

interface PreviewProps {
    handle: string;
    avatar?: string;
    imageUrls: string[];
    imageKinds?: (string | undefined)[];
    caption: string;
    assetsLoading?: boolean;
    accountLoading?: boolean;
}

const InstagramPreview: React.FC<PreviewProps> = ({
    handle, avatar, imageUrls, imageKinds, caption, assetsLoading, accountLoading,
}) => (
    <div>
        <div className="flex items-center gap-2 px-3 py-2">
            {/* Instagram's story-ring gradient is the platform's own mark */}
            <div
                className="flex size-[30px] items-center justify-center rounded-full p-[2px]"
                style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}
            >
                {accountLoading ? (
                    <Skeleton className="size-[26px] rounded-full" />
                ) : (
                    <Avatar src={avatar} handle={handle} size={26} />
                )}
            </div>
            {accountLoading ? (
                <Skeleton className="h-4 w-20" />
            ) : (
                <span className="text-[13px] font-medium text-foreground" style={{ fontFamily: NATIVE_FONT }}>{handle}</span>
            )}
            <div className="flex-1" />
            <MoreHorizontal className="size-[18px] text-foreground" />
        </div>
        <ImageCarousel urls={imageUrls} kinds={imageKinds} aspectRatio="1/1" loading={assetsLoading} />
        <div className="flex items-center gap-3 px-3 py-2 text-foreground">
            <Heart className="size-[22px]" />
            <MessageCircle className="size-5" />
            <Send className="size-5 -translate-y-0.5 -rotate-[25deg]" />
            <div className="flex-1" />
            <Bookmark className="size-[22px]" />
        </div>
        <div className="px-3 pb-3">
            <CaptionText text={caption ? `${handle} ${caption}` : handle} lines={3} fontSize={13} />
        </div>
    </div>
);

/* ═══ FACEBOOK / META ═════════════════════════════════════════ */

const FacebookPreview: React.FC<PreviewProps> = ({
    handle, avatar, imageUrls, imageKinds, caption, assetsLoading, accountLoading,
}) => (
    <div>
        <div className="flex items-center gap-2 px-3 py-2.5">
            {accountLoading ? (
                <Skeleton className="size-9 rounded-full" />
            ) : (
                <Avatar src={avatar} handle={handle} size={36} />
            )}
            <div>
                {accountLoading ? (
                    <>
                        <Skeleton className="mb-1 h-4 w-28" />
                        <Skeleton className="h-3 w-20" />
                    </>
                ) : (
                    <>
                        <div className="text-sm font-medium text-foreground" style={{ fontFamily: NATIVE_FONT }}>{handle}</div>
                        <div className="text-xs text-muted-foreground" style={{ fontFamily: NATIVE_FONT }}>Just now · 🌐</div>
                    </>
                )}
            </div>
            <div className="flex-1" />
            <MoreHorizontal className="size-[18px] text-muted-foreground" />
        </div>
        {caption && (
            <div className="px-3 pb-2">
                <CaptionText text={caption} lines={3} fontSize={14} />
            </div>
        )}
        <ImageCarousel urls={imageUrls} kinds={imageKinds} aspectRatio="16/9" loading={assetsLoading} />
        <div className="flex items-center justify-around border-t px-3 py-2">
            {[{ icon: ThumbsUp, label: 'Like' }, { icon: MessageCircle, label: 'Comment' }, { icon: Share2, label: 'Share' }].map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground" style={{ fontFamily: NATIVE_FONT }}>
                    <Icon className="size-[18px]" /> {label}
                </span>
            ))}
        </div>
    </div>
);

/* ═══ TIKTOK ══════════════════════════════════════════════════ */

const TikTokPreview: React.FC<PreviewProps> = ({
    handle, imageUrls, imageKinds, caption, assetsLoading, accountLoading,
}) => {
    const [idx, setIdx] = useState(0);
    const url = imageUrls[idx] || '';
    const kind = imageKinds?.[idx];
    const count = imageUrls.length;
    const hasMultiple = count > 1;
    const isVideo = kind === 'video';

    return (
        <div className="relative">
            <div
                className="relative aspect-[9/14] w-full overflow-hidden bg-black bg-cover bg-center"
                style={{ backgroundImage: !isVideo && url ? `url(${url})` : undefined }}
            >
                {isVideo && url && (
                    <video
                        src={url}
                        muted
                        autoPlay
                        loop
                        playsInline
                        className="absolute inset-0 size-full object-cover"
                    />
                )}
                {assetsLoading && <Skeleton className="absolute inset-0 bg-white/10" />}
                {!assetsLoading && !url && (
                    <div className="absolute inset-0 flex items-center justify-center text-4xl text-white/20">🖼</div>
                )}

                {/* Multi-image arrows */}
                {hasMultiple && (
                    <>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + count) % count); }}
                            className="absolute start-1 top-1/2 flex size-[22px] -translate-y-1/2 items-center justify-center rounded-full bg-white/30 text-white"
                        >
                            <ChevronLeft className="size-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % count); }}
                            className="absolute end-10 top-1/2 flex size-[22px] -translate-y-1/2 items-center justify-center rounded-full bg-white/30 text-white"
                        >
                            <ChevronRight className="size-3.5" />
                        </button>
                        <div className="absolute end-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white" style={{ fontFamily: NATIVE_FONT }}>
                            {idx + 1}/{count}
                        </div>
                    </>
                )}

                {/* Right action bar */}
                <div className="absolute end-2 bottom-[60px] flex flex-col items-center gap-4">
                    {[Heart, MessageCircle, Bookmark, Share2].map((Icon, i) => (
                        <div key={i} className="flex flex-col items-center drop-shadow">
                            <Icon className="size-6 text-white" />
                            <span className="mt-0.5 text-[10px] text-white" style={{ fontFamily: NATIVE_FONT }}>0</span>
                        </div>
                    ))}
                    <div className="flex size-7 items-center justify-center rounded-full border-2 border-white/30 bg-white/20">
                        <Music className="size-3 text-white" />
                    </div>
                </div>

                {/* Bottom overlay */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
                    {accountLoading ? (
                        <Skeleton className="h-4 w-24 bg-white/20" />
                    ) : (
                        <div className="mb-1 text-sm font-medium text-white" style={{ fontFamily: NATIVE_FONT }}>@{handle}</div>
                    )}
                    <CaptionText text={caption} lines={2} fontSize={12} color="#FFF" />
                    <div className="mt-1 flex items-center gap-1.5">
                        <Music className="size-3 text-white" />
                        <span className="text-[11px] text-white" style={{ fontFamily: NATIVE_FONT }}>Original sound - {handle}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ═══ LINKEDIN ════════════════════════════════════════════════ */

const LinkedInPreview: React.FC<PreviewProps> = ({
    handle, avatar, imageUrls, imageKinds, caption, assetsLoading, accountLoading,
}) => (
    <div>
        <div className="flex items-start gap-2 px-3 py-2.5">
            {accountLoading ? (
                <Skeleton className="size-10 rounded-full" />
            ) : (
                <Avatar src={avatar} handle={handle} size={40} />
            )}
            <div className="flex-1">
                {accountLoading ? (
                    <>
                        <Skeleton className="mb-1 h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                    </>
                ) : (
                    <>
                        <div className="text-sm font-medium text-foreground" style={{ fontFamily: NATIVE_FONT }}>{handle}</div>
                        <div className="text-xs leading-tight text-muted-foreground" style={{ fontFamily: NATIVE_FONT }}>Just now · 🌐</div>
                    </>
                )}
            </div>
            <MoreHorizontal className="size-[18px] text-muted-foreground" />
        </div>
        {caption && (
            <div className="px-3 pb-2">
                <CaptionText text={caption} lines={3} fontSize={14} />
            </div>
        )}
        <ImageCarousel urls={imageUrls} kinds={imageKinds} aspectRatio="16/9" loading={assetsLoading} />
        <div className="flex items-center justify-around border-t px-3 py-2">
            {[{ icon: ThumbsUp, label: 'Like' }, { icon: MessageCircle, label: 'Comment' }, { icon: Repeat2, label: 'Repost' }, { icon: Send, label: 'Send' }].map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground" style={{ fontFamily: NATIVE_FONT }}>
                    <Icon className="size-4" /> {label}
                </span>
            ))}
        </div>
    </div>
);
